import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { FLOW_EXISTING, resolveConsumerFlow } from '@/lib/serviceConfig';

const cache = new Map();
const CACHE_MS = 5 * 60 * 1000;

/**
 * Fetch service configuration for operational routing.
 * Fail-safe: returns null on any error (caller should use existing flow).
 */
export function useServiceConfig(serviceKey) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!serviceKey) {
      setConfig(null);
      setLoading(false);
      return;
    }
    const cached = cache.get(serviceKey);
    if (cached && Date.now() - cached.at < CACHE_MS) {
      setConfig(cached.data);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/broadcast/services/${serviceKey}`);
      const svc = data.service || null;
      cache.set(serviceKey, { data: svc, at: Date.now() });
      setConfig(svc);
      setError(null);
    } catch (e) {
      setConfig(null);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [serviceKey]);

  useEffect(() => {
    load();
  }, [load]);

  const flow = resolveConsumerFlow(config);
  const isBroadcast = flow !== FLOW_EXISTING;

  return { config, loading, error, flow, isBroadcast, reload: load };
}

/**
 * Load full schema for broadcast request forms.
 * Fail-safe: returns null on error.
 */
export async function fetchServiceSchema(serviceKey) {
  try {
    const { data } = await api.get(`/broadcast/services/${serviceKey}/schema`);
    return data;
  } catch {
    return null;
  }
}

export function clearServiceConfigCache() {
  cache.clear();
}
